import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchRoles, fetchOrganizations, fetchOrgMembers } from '../api/auth0Api';
import { useAuth0 } from '../components/auth/DevAuth0Provider';

const ClientsDataContext = createContext();

export const ClientsProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgMembers, setOrgMembers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Retry function with exponential backoff
  const retryWithBackoff = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        console.warn(`Attempt ${i + 1} failed:`, error.message);
        if (i === maxRetries - 1) throw error;
        
        // Exponential backoff: 1s, 2s, 4s
        const backoffDelay = delay * Math.pow(2, i);
        // console.log(`Retrying in ${backoffDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
  };

  // Sequential fetching of org members to avoid overwhelming backend
  const fetchAllOrgMembers = async (orgsData) => {
    const membersMap = {};
    
    for (const org of orgsData) {
      try {
        // console.log(`Fetching members for org: ${org.name || org.id}`);
        const members = await retryWithBackoff(
          () => fetchOrgMembers(org.id, getAccessTokenSilently),
          3, // max retries
          500 // initial delay
        );
        membersMap[org.id] = members;
        
        // Small delay between requests to be gentle on the backend
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Failed to fetch members for org ${org.name || org.id}:`, error);
        // Continue with other orgs even if one fails
        membersMap[org.id] = [];
      }
    }
    
    return membersMap;
  };

  const loadClientsData = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // console.log("ClientsDataContext: Loading clients data with authentication");
      
      // Fetch roles and organizations with retry logic
      const [rolesData, orgsData] = await Promise.all([
        retryWithBackoff(() => fetchRoles(getAccessTokenSilently)),
        retryWithBackoff(() => fetchOrganizations(getAccessTokenSilently)),
      ]);
      
      setRoles(rolesData || []);
      setOrganizations(orgsData || []);

      // Fetch org members sequentially to avoid backend overload
      if (orgsData && orgsData.length > 0) {
        // console.log(`Fetching members for ${orgsData.length} organizations sequentially...`);
        const membersMap = await fetchAllOrgMembers(orgsData);
        setOrgMembers(membersMap);
      } else {
        setOrgMembers({});
      }
      
      // console.log("ClientsDataContext: Successfully loaded all data");
    } catch (err) {
      console.error("Failed to load clients data in context:", err);
      setError(err);
      // Clear data on error to prevent stale state
      setRoles([]);
      setOrganizations([]);
      setOrgMembers({});
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  // Debounced version to prevent rapid successive calls
  const debouncedLoadClientsData = useCallback(() => {
    const timeoutId = setTimeout(() => {
      loadClientsData();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [loadClientsData]);

  useEffect(() => {
    // Only load data if authenticated and not loading
    if (isAuthenticated) {
      const cleanup = debouncedLoadClientsData();
      return cleanup;
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, debouncedLoadClientsData]);

  const value = {
    roles,
    organizations,
    orgMembers,
    isLoading,
    error,
    refreshClientsData: loadClientsData // Expose the non-debounced version for manual refresh
  };

  return (
    <ClientsDataContext.Provider value={value}>
      {children}
    </ClientsDataContext.Provider>
  );
};

export const useClientsData = () => useContext(ClientsDataContext);